import forge from 'node-forge';
import { hexToBuffer } from './helpers';
import { getKeyFromPem } from './pemExtractors';
// Fallback to old PKCS#1 v1.5
const decryptPkcs1v15 = async (hex, prvKey)=>{
    // 1. We extract the raw DER bytes
    const derBuffer = await window.crypto.subtle.exportKey('pkcs8', prvKey);
    // 2. Safe ArrayBuffer -> Binary String conversion for node-forge
    const bytes = new Uint8Array(derBuffer);
    let binaryString = '';
    for(let i = 0; i < bytes.length; i++){
        binaryString += String.fromCharCode(bytes[i]);
    }
    // 3. Parsing the ASN.1 structure
    const asn1 = forge.asn1.fromDer(binaryString);
    // 4. Importing a PKCS#8 private key in node-forge
    let privateKey;
    try {
        privateKey = forge.pki.privateKeyFromAsn1(asn1);
    } catch (e) {
        const pkcs8 = forge.pkcs8.privateKeyFromAsn1(asn1);
        privateKey = pkcs8.privateKey || pkcs8;
    }
    // 5. PKCS#1 v1.5 decryption
    const encryptedBytes = forge.util.hexToBytes(hex);
    const decrypted = privateKey.decrypt(encryptedBytes, 'RSAES-PKCS1-V1_5');
    return forge.util.decodeUtf8(decrypted);
};
// Sometimes, jsrsasign generates MGF1 padding when calling RSAOAEP512 in a way that the Web Crypto API rejects
// (due to the strict OAEP label). For this case, we have a helper function decryptWithForgeOAEP.
const decryptWithForgeOAEP = async (hex, prvKey, mdHash = 'SHA512')=>{
    const derBuffer = await window.crypto.subtle.exportKey('pkcs8', prvKey);
    const bytes = new Uint8Array(derBuffer);
    let binaryString = '';
    for(let i = 0; i < bytes.length; i++){
        binaryString += String.fromCharCode(bytes[i]);
    }
    const asn1 = forge.asn1.fromDer(binaryString);
    let privateKey;
    try {
        privateKey = forge.pki.privateKeyFromAsn1(asn1);
    } catch (e) {
        const pkcs8 = forge.pkcs8.privateKeyFromAsn1(asn1);
        privateKey = pkcs8.privateKey || pkcs8;
    }
    const encryptedBytes = forge.util.hexToBytes(hex);
    // We choose the appropriate hash function in node-forge (default: SHA512)
    const md = forge.md[mdHash.toLowerCase()].create();
    const mgf1 = forge.mgf.mgf1.create(forge.md[mdHash.toLowerCase()].create());
    const decrypted = privateKey.decrypt(encryptedBytes, 'RSA-OAEP', {
        md,
        mgf1
    });
    return forge.util.decodeUtf8(decrypted);
};
/* eslint-disable no-await-in-loop */ export const decrypt = async (hex, prvKey, passphrase = null)=>{
    const { subtle } = window.crypto;
    // If the key is passed in PEM format (string), we convert it to CryptoKey
    const prvCryptoKey = typeof prvKey === 'string' ? await getKeyFromPem(prvKey, passphrase) : prvKey;
    const cleanHex = hex.startsWith('0x') ? hex.slice(2) : hex;
    const modulusBytes = (prvCryptoKey.algorithm.modulusLength || 2048) / 8;
    const normalizedHex = cleanHex.padStart(modulusBytes * 2, '0');
    const encryptedData = hexToBuffer(normalizedHex);
    // 1. First quick decryption attempt with the passed CryptoKey object
    try {
        const decryptedBuffer = await subtle.decrypt({
            name: 'RSA-OAEP'
        }, prvCryptoKey, encryptedData);
        return new TextDecoder().decode(decryptedBuffer);
    } catch (e) {
    // We move on to testing specific hash functions (SHA-512, SHA-256, SHA-1)
    }
    // 2. Re-import key for different Hash variants (SHA-512, SHA-384, SHA-256, SHA-1)
    // jsrsasign 'RSAOAEP512' required an explicit call to RSA-OAEP with SHA-512
    try {
        const exportedPkcs8 = await subtle.exportKey('pkcs8', prvCryptoKey);
        const hashes = [
            'SHA-512',
            'SHA-256',
            'SHA-1',
            'SHA-384'
        ];
        for (const hash of hashes){
            try {
                const reimportedKey = await subtle.importKey('pkcs8', exportedPkcs8, {
                    name: 'RSA-OAEP',
                    hash
                }, true, [
                    'decrypt'
                ]);
                const decryptedBuffer = await subtle.decrypt({
                    name: 'RSA-OAEP'
                }, reimportedKey, encryptedData);
                return new TextDecoder().decode(decryptedBuffer);
            } catch (err) {
            // Trying with another hash
            }
        }
    } catch (exportErr) {
    // Trying with node-forge
    }
    // 3. Fallback for node-forge (if the Web Crypto API rejected the specific MGF1 format from jsrsasign)
    try {
        const decryptedText = await decryptWithForgeOAEP(normalizedHex, prvCryptoKey, 'SHA512');
        return decryptedText;
    } catch (forgeOAEP3Err) {
        // Attempting to fall back to old PKCS#1 v1.5
        try {
            const decryptedText = await decryptPkcs1v15(normalizedHex, prvCryptoKey);
            return decryptedText;
        } catch (forgeErr) {
        // Exception ignored
        }
    }
    throw new Error('OperationError: Failed to decrypt data with the selected key pair.');
};
